import axios from 'axios';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Icon,
  LoaderCircle,
  Tooltip,
  UtilityInlineGrid,
  UtilityList,
} from 'rhinostyle';
import {
  fetchContactScheduledMessages,
  cancelScheduledMessage,
  updateScheduledMessage,
} from '../reducers/ScheduledMessageReducer';
import { formatName, userHasAnyOfPermissions } from '../helpers/UserHelpers';
import { UserPermissionsConstants } from '../constants';
import { PROFILE_SUMMARY_APPOINTMENTS_LIST_SIZE } from '../constants/AppConstants';
import CancelScheduledMessageModal from './CancelScheduledMessageModal';
import ScheduledMessageModal from './ScheduledMessageModal';
import NotificationService from '../services/NotificationService';

const ContactSummaryScheduledMessagesSection = (props) => {
  const {
    activeUser,
    contactScheduledMessages,
    isContactLoading,
    isScheduledMessagesSectionOpen,
    openCloseScheduledMessagesSection,
  } = props;

  const dispatch = useDispatch();
  const currentUserId = useSelector((state) => state.auth.currentUser);

  const [displayLength, setDisplayLength] = useState(PROFILE_SUMMARY_APPOINTMENTS_LIST_SIZE);
  const [editingMessage, setEditingMessage] = useState(null);
  const [editingPhones, setEditingPhones] = useState([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [cancellingId, setCancellingId] = useState(null);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  useEffect(() => {
    if (activeUser.id) {
      dispatch(fetchContactScheduledMessages(activeUser.id));
      setDisplayLength(PROFILE_SUMMARY_APPOINTMENTS_LIST_SIZE);
    }
  }, [activeUser.id]);

  // Two-step open: mount modal with open=false first (key change), then transition to open=true
  useEffect(() => {
    if (editingMessage) {
      setIsEditModalOpen(true);
    }
  }, [editingMessage]);

  const handleShowMore = () => {
    setDisplayLength((current) => current + PROFILE_SUMMARY_APPOINTMENTS_LIST_SIZE);
  };

  const handleEditClick = (e, message) => {
    e.stopPropagation();
    setIsEditModalOpen(false);
    setEditingMessage(message);
    setEditingPhones([]);
    axios.get(`/users/${activeUser.id}`)
      .then((response) => setEditingPhones((response.data?.phones || []).filter((p) => Number(p.ownerId) === Number(activeUser.id))))
      .catch(() => {});
  };

  const handleEditClose = () => {
    setIsEditModalOpen(false);
    setEditingMessage(null);
    setEditingPhones([]);
  };

  const handleEditConfirm = (scheduledAt, messageText, phoneId) => {
    dispatch(updateScheduledMessage(editingMessage.id, { scheduledAt, messageText, phoneId }))
      .then((response) => {
        NotificationService('updateScheduledMessage', response || { status: 200 });
        dispatch(fetchContactScheduledMessages(activeUser.id));
      });
    setIsEditModalOpen(false);
    setEditingMessage(null);
    setEditingPhones([]);
  };

  const handleCancelClick = (e, id) => {
    e.stopPropagation();
    setCancellingId(id);
    setIsCancelModalOpen(true);
  };

  const handleCancelConfirm = () => {
    dispatch(cancelScheduledMessage(cancellingId))
      .then(() => {
        dispatch(fetchContactScheduledMessages(activeUser.id));
      });
    setIsCancelModalOpen(false);
    setCancellingId(null);
  };

  const handleCancelClose = () => {
    setIsCancelModalOpen(false);
    setCancellingId(null);
  };

  const userTimezone = moment.tz.guess();

  const formatScheduledAt = (scheduledAt) => moment.utc(scheduledAt).tz(userTimezone).format('ddd, MMM D · h:mm a z');

  const ALERT_LABELS = {
    phone_deleted: 'Phone deleted',
    opted_out: 'Contact opted out',
  };

  const tooltipContent = (message) => (
    <div className="analytics__tooltip-box">
      <p className="analytics__tooltip-message">{message.messageText}</p>
    </div>
  );

  const renderRow = (message) => (
    <div key={message.id} className="hipaa-status--summary">
      <div className="u-flex u-flex-align-items-center">
        <div className="u-flex u-flex-align-items-center u-m-r-small u-flex-shrink-0">
          <Tooltip content={tooltipContent(message)} delay={false} placement="top">
            <Icon icon="note" className="u-text-primary" />
          </Tooltip>
        </div>
        <div className="u-flex-1">
          <div className="u-flex u-flex-align-items-center">
            <strong>{formatScheduledAt(message.scheduledAt)}</strong>
            {message.status === 'alert' && (
              <Tooltip placement="top" content={ALERT_LABELS[message.alertType] || 'Alert'}>
                <span><Icon icon="alert-triangle" className="u-text-danger u-m-l-small" /></span>
              </Tooltip>
            )}
          </div>
          <div className="u-text-muted u-text-small">{message.sender ? formatName(message.sender) : '—'}</div>
        </div>
        {(userHasAnyOfPermissions([UserPermissionsConstants.SCHEDULED_MESSAGE_EDIT]) || message.senderId === currentUserId) && (
          <UtilityInlineGrid>
            <Tooltip content="Edit" placement="top">
              <Button type="link" size="small" iconOnly onClick={(e) => handleEditClick(e, message)}>
                <Icon icon="create" />
              </Button>
            </Tooltip>
            <Tooltip content="Cancel" placement="top">
              <Button type="link" size="small" iconOnly onClick={(e) => handleCancelClick(e, message.id)}>
                <Icon icon="trash" className="u-text-danger" />
              </Button>
            </Tooltip>
          </UtilityInlineGrid>
        )}
      </div>
    </div>
  );

  const now = moment();
  const visibleMessages = contactScheduledMessages.filter((m) =>
    m.status === 'alert' || moment.utc(m.scheduledAt).isAfter(now));

  const renderContent = () => {
    if (isContactLoading) {
      return <LoaderCircle className="u-text-center u-p-t u-p-b" />;
    }
    if (!visibleMessages.length) {
      return (
        <div className="u-text-italic u-p-t u-p-r u-p-b">
          No pending scheduled messages for this contact
        </div>
      );
    }
    return (
      <UtilityList space className="u-m-b-0 u-m-t">
        {visibleMessages.slice(0, displayLength).map(renderRow)}
      </UtilityList>
    );
  };

  const showMoreVisible = !isContactLoading && isScheduledMessagesSectionOpen && displayLength < visibleMessages.length;

  return (
    <div className="summary-panel__section summary-panel__section--full">
      <div className="summary-panel__section__label">
        Scheduled Messages
        <div className="summary-panel__section__label__action">
          <Icon
            icon={isScheduledMessagesSectionOpen ? 'caret-up' : 'caret-down'}
            className="u-text-muted"
            onClick={openCloseScheduledMessagesSection}
          />
        </div>
      </div>
      {isScheduledMessagesSectionOpen && (
        <div className="summary-panel__section__content summary-panel__section__content--indented">
          {renderContent()}
        </div>
      )}
      {showMoreVisible && (
        <div className="hipaa-status__show-more">
          <Button reset onClick={handleShowMore}>Show More</Button>
        </div>
      )}

      <ScheduledMessageModal
        key={editingMessage ? editingMessage.id : 'summary-edit-modal'}
        isOpen={isEditModalOpen}
        initialScheduledAt={editingMessage && editingMessage.scheduledAt}
        initialMessageText={editingMessage && editingMessage.messageText}
        phones={editingPhones}
        selectedPhoneId={editingMessage && editingMessage.phoneId}
        onClose={handleEditClose}
        onConfirm={handleEditConfirm}
      />

      <CancelScheduledMessageModal
        open={isCancelModalOpen}
        onClose={handleCancelClose}
        onConfirm={handleCancelConfirm}
      />
    </div>
  );
};

ContactSummaryScheduledMessagesSection.propTypes = {
  activeUser: PropTypes.object.isRequired,
  contactScheduledMessages: PropTypes.array.isRequired,
  isContactLoading: PropTypes.bool.isRequired,
  isScheduledMessagesSectionOpen: PropTypes.bool.isRequired,
  openCloseScheduledMessagesSection: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  contactScheduledMessages: state.scheduledMessage.contactScheduledMessages,
  isContactLoading: state.scheduledMessage.isContactLoading,
});

export default connect(mapStateToProps)(ContactSummaryScheduledMessagesSection);
